// TTLs match Ember referral-storage.js
const STW_TTL_MS = 2 * 60 * 60 * 1000 // 2 hours
const OWG_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export type ReferralSource = 'skytabwebsite' | 'owg'

interface ReferralConfig {
	ttl: number
	singleUse: boolean
}

const REFERRAL_CONFIGS: Record<ReferralSource, ReferralConfig> = {
	skytabwebsite: { ttl: STW_TTL_MS, singleUse: true },
	owg: { ttl: OWG_TTL_MS, singleUse: false },
}

export interface StoredReferral {
	source: ReferralSource
	value: string
	timestamp: number
	expiresAt: number
	singleUse: boolean
	locationRef: string
}

function hasLocalStorage(): boolean {
	const probe = '__ls_probe__'
	try {
		localStorage.setItem(probe, probe)
		localStorage.removeItem(probe)
		return true
	} catch {
		return false
	}
}

function isValidStoredReferral(v: unknown): v is StoredReferral {
	return (
		typeof v === 'object' &&
		v !== null &&
		typeof (v as Record<string, unknown>).expiresAt === 'number' &&
		typeof (v as Record<string, unknown>).value === 'string' &&
		typeof (v as Record<string, unknown>).source === 'string'
	)
}

function storageKey(source: ReferralSource, locationRef: string): string {
	return `_referral_${source}_${locationRef}`
}

export function setReferral(locationRef: string, source: ReferralSource, value: string): void {
	if (!hasLocalStorage() || !locationRef || !value) return

	// Callers may cast unknown sources to ReferralSource — guard against runtime unknowns.
	const config = REFERRAL_CONFIGS[source] as ReferralConfig | undefined
	if (!config) return

	const now = Date.now()

	try {
		localStorage.setItem(
			storageKey(source, locationRef),
			JSON.stringify({
				source,
				value,
				timestamp: now,
				expiresAt: now + config.ttl,
				singleUse: config.singleUse,
				locationRef,
			} satisfies StoredReferral),
		)
	} catch {
		// Silent — private browsing or storage quota exceeded
	}
}

export function getReferral(locationRef: string, source: ReferralSource): StoredReferral | null {
	if (!hasLocalStorage() || !locationRef) return null

	const key = storageKey(source, locationRef)
	const raw = localStorage.getItem(key)
	if (!raw) return null

	try {
		const parsed: unknown = JSON.parse(raw)

		if (!isValidStoredReferral(parsed)) {
			localStorage.removeItem(key)
			return null
		}

		if (parsed.expiresAt < Date.now()) {
			localStorage.removeItem(key)
			return null
		}

		return parsed
	} catch {
		localStorage.removeItem(key)
		return null
	}
}

// Extends the STW referral's expiresAt whenever the cart is saved — matches Ember's
// touchReferral call inside set-local-storage.js.
export function touchReferral(locationRef: string, source: ReferralSource): void {
	if (!hasLocalStorage() || !locationRef) return

	const config = REFERRAL_CONFIGS[source]
	const key = storageKey(source, locationRef)
	const raw = localStorage.getItem(key)
	if (!raw) return

	try {
		const parsed: unknown = JSON.parse(raw)

		if (!isValidStoredReferral(parsed)) {
			localStorage.removeItem(key)
			return
		}

		if (parsed.expiresAt < Date.now()) {
			localStorage.removeItem(key)
			return
		}

		parsed.expiresAt = Date.now() + config.ttl
		localStorage.setItem(key, JSON.stringify(parsed))
	} catch {
		localStorage.removeItem(key)
	}
}

export function clearReferral(locationRef: string, source: ReferralSource): void {
	if (!hasLocalStorage() || !locationRef) return

	try {
		localStorage.removeItem(storageKey(source, locationRef))
	} catch {
		// Silent
	}
}
